import React, { useRef, useEffect, useState, useMemo } from 'react'
import style from './styles.module.scss';
import { useMediaQuery } from 'react-responsive';
import { ArrowLeftIcon, TrendIcon } from '@features/ui/Icons/';
import cN from 'classnames';
import { RootState } from '@Redux/search/store';
import { setSearchPrice, setUrlparams } from '@Redux/search/actions';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import Skeleton from '@features/skeletons';
import OutOfRangeDateLightBox from '@features/lightbox/OutOfRangeDateLightBox';
import { getItineraryUrlParams } from '@features/searchPanel/utils';
interface CarouselData {
    departDate: string;
    returnDate: string | null;
    price: string | number | null;
    isSelected: boolean
}

interface FlightRateCarouselProps {
    handleTrendModalOpen: () => void
}

export default function FlightRateCarousel({ handleTrendModalOpen }: FlightRateCarouselProps) {
    const [isInitialized, setIsInitialized] = useState(false)
    const [clickItemCondition, setClickItemCondition] = useState(true)
    const [currentTranslate, setCurrentTranslate] = useState(0)
    const [carouselData, setCarouselData] = useState<CarouselData[]>([])
    const [dateWrapWidth, setDateWrapWidth] = useState(0)
    const [dateCellWidth, setDateCellWidth] = useState(0)
    const [openAlertModal, setOpenAlertModal] = useState(false)
    const dateContainerRef = useRef<HTMLDivElement>(null);
    const dateCellRef = useRef<HTMLDivElement>(null);
    const dateWrapRef = useRef<HTMLDivElement>(null);
    const clickedAmount = useRef(0)
    const timer = useRef<NodeJS.Timeout | null>(null); // Use useRef for timer
    const dispatch = useDispatch();
    const { SearchPrice, SearchInfoCalendar, UrlParams } = useSelector((state: RootState) => state.search);
    const maxTranslateX = 0
    const minTranslateX = useMemo(() => {
        return -((dateCellWidth * 14) - dateWrapWidth)
    }, [dateCellWidth, dateWrapWidth])
    const [selectedDepartDate, setSelectedDepartDate] = useState("")

    useEffect(() => {
        if (dateWrapRef.current) {  // 取得輪播可視區塊的寬度(<div classname='date-wrap'></div>)
            setDateWrapWidth(dateWrapRef.current.offsetWidth)
        }
    }, [dateWrapRef.current])

    useEffect(() => {
        if (dateCellRef.current) { // 取得每個日期的寬度(<div classname='date-cell'></div>)
            setDateCellWidth(dateCellRef.current.offsetWidth)
        }
    }, [dateCellRef.current])

    useEffect(() => {  // 初始化輪播，使選取的日期位於中間。若視窗resize事件觸發，或選擇新日期後，也會重新初始化輪播
        initializeCarousel() // pc版時選取日期位於中間，m版時選取日期位於最左邊
    }, [dateWrapWidth, dateCellWidth, dateContainerRef.current, carouselData])

    useEffect(() => {
        // 當視窗大小改變時，重新取得輪播可視區塊的寬度
        const getDateWrapWidth = () => {
            if (dateWrapRef.current) {
                setDateWrapWidth(dateWrapRef.current.offsetWidth)
            }
        }
        const debouncedGetDateWrapWidth = debounce(getDateWrapWidth, 1000);
        window.addEventListener('resize', debouncedGetDateWrapWidth);
        return () => {
            window.removeEventListener('resize', debouncedGetDateWrapWidth);
        };
    }, [])

    useEffect(() => {
        setSelectedDepartDate(UrlParams.SeekDestinations[0].DepartDate)
    }, [UrlParams.SeekDestinations])

    useEffect(() => {  // 1  先顯示日期，等待api取得價格
        if (selectedDepartDate === "") return
        const isRoundTrip = UrlParams.SeekDestinations.length === 2
        const selectedReturnDate = isRoundTrip ? UrlParams.SeekDestinations[1].DepartDate : null;
        const differenceInDays = isRoundTrip ? dayjs(selectedReturnDate).diff(dayjs(selectedDepartDate), 'day') : null

        const formattedSelectedDepartDate = dayjs(selectedDepartDate)
        const firstDate = formattedSelectedDepartDate.subtract(6, 'day')
        const dateListed = []
        for (let i = 0; i < 14; i++) {
            dateListed.push(firstDate.add(i, 'day').format('YYYY-MM-DD'))
        }
        const defaultCarouselData = dateListed.map((date) => {
            return {
                departDate: date,
                returnDate: isRoundTrip ? dayjs(date).add(differenceInDays!, 'day').format('YYYY-MM-DD') : null,
                price: null,
                isSelected: false
            }
        })

        defaultCarouselData.forEach((item) => {
            if (selectedDepartDate === item.departDate) {
                item.isSelected = true
            }
        })
        setCarouselData([...defaultCarouselData])
        setIsInitialized(true)
    }, [selectedDepartDate])

    useEffect(() => {  // 2 塞入來自api的價格資訊，待isInitialized為true時才需執行effect
        if (SearchInfoCalendar.data && isInitialized) {
            const flightInfos = [...JSON.parse(JSON.stringify(SearchInfoCalendar.data)).FlightInfos]
            const formattedFlightInfos = flightInfos.map((item) => {
                return {
                    departDate: item.ItineraryInfos[0].DepDateTime.split("T")[0],
                    returnDate: item.ItineraryInfos[1] ? item.ItineraryInfos[1].DepDateTime.split("T")[0] : null,
                    price: item.FareInfos[0].AdtPrice,
                    isSelected: false
                }
            })

            const priceUpdatedCarouselData = carouselData.map((item) => {
                formattedFlightInfos.forEach((singleInfo) => {
                    if (item.departDate === singleInfo.departDate) {
                        item.price = singleInfo.price
                    }
                })
                return item
            })

            setCarouselData([...priceUpdatedCarouselData])
            setIsInitialized(false)
        }
    }, [SearchInfoCalendar.data])

    // 確認出發日及回程日是否在系統可選日期範圍內
    function dateInvalid(selectedDate: CarouselData) {
        const minDate = dayjs().add(1, 'day').startOf('day')
        const maxDate = dayjs().add(360, 'day').startOf('day')
        // 若出發日早於最小可選日期，或回程日早於最小可選日期，則回傳true
        const isBeforeMinDay = dayjs(selectedDate.departDate).isBefore(minDate) || (selectedDate.returnDate ? dayjs(selectedDate.returnDate).isBefore(minDate) : false)
        // 若出發日晚於最大可選日期，或回程日晚於最大可選日期，則回傳true
        const isAfterMaxDay = dayjs(selectedDate.departDate).isAfter(maxDate) || (selectedDate.returnDate ? dayjs(selectedDate.returnDate).isAfter(maxDate) : false) // 若回程日為null，則直接回傳false

        if (isBeforeMinDay || isAfterMaxDay) {
            return true  // true代表日期不合規定，因日期不在可選範圍區間內
        } else {
            return false
        }
    }

    function moveHorizontally(direction: string, cells: number): void {
        // 點擊左右箭頭時，移動輪播
        if (typeof dateWrapWidth !== 'number') return;
        const moveRange = dateCellWidth * cells
        if (direction === "clickLeft") {
            if (dateContainerRef.current) {
                let translate = currentTranslate + moveRange
                if (translate >= maxTranslateX) {
                    translate = 0
                }
                setCurrentTranslate(translate)
                clickedAmount.current = 0
            }
        } else if (direction === "clickRight") {
            if (dateContainerRef.current) {
                clickedAmount.current += 1
                if (clickedAmount.current >= 2) {
                    handleTrendModalOpen()
                    return
                }
                let translate = currentTranslate - moveRange
                if (translate < minTranslateX) {
                    translate = minTranslateX
                }
                setCurrentTranslate(translate)
            }
        }
    }

    function initializeCarousel() {
        let selectedIndex: number = 6
        carouselData.forEach((item, index) => {
            if (item.isSelected) {
                selectedIndex = index
            }
        })
        if (dateWrapWidth && dateCellWidth) {
            if (window.innerWidth >= 980) {
                if (dateContainerRef.current) {  // pc版只能透過點擊箭頭移動，不可透過x軸scroll移動
                    dateWrapRef.current && dateWrapRef.current.scrollTo({
                        left: 0,
                        behavior: 'auto'
                    });
                    let translate = -(dateCellWidth * (selectedIndex - Math.floor((dateWrapWidth / dateCellWidth) / 2)))
                    if (translate >= maxTranslateX) {
                        translate = 0
                    } else if (translate < minTranslateX) {
                        translate = minTranslateX
                    }
                    setCurrentTranslate(translate)
                    clickedAmount.current = 0
                }
            } else {
                if (dateContainerRef.current) {  // m版只能透過x軸scroll移動，箭頭按鈕隱藏
                    setCurrentTranslate(0)
                }
                dateWrapRef.current && dateWrapRef.current.scrollTo({
                    left: dateCellWidth * selectedIndex,
                    behavior: 'smooth'
                });
                clickedAmount.current = 0
            }
        }
    }

    function clickItem(clickedIndex: number, isInvalidDate: boolean) {
        // 點擊特定日期時，改變搜尋結果區塊的項目
        if (SearchInfoCalendar.isLoading) return
        if (clickItemCondition) {
            setClickItemCondition(false)

            const carouselNew = carouselData.map((item, index) => {
                if (index === clickedIndex) {
                    item.isSelected = true
                } else {
                    item.isSelected = false
                }
                return item
            })
            setCarouselData([...carouselNew])
            // initializeCarousel()

            // 更新url中的日期參數
            const url = new URL(window.location.href)
            const params = new URLSearchParams(url.search)
            params.set('DepDate1', carouselData[clickedIndex].departDate)
            if (typeof carouselData[clickedIndex].returnDate === 'string') {
                params.set('DepDate2', (carouselData[clickedIndex].returnDate)! as string ? (carouselData[clickedIndex].returnDate)! as string : '')
            }
            url.search = params.toString()
            window.history.pushState({}, '', url.toString())

            // 依點擊的日期重新取得價格資訊
            const selectedDate = carouselNew.filter((item) => {
                return item.isSelected
            })
            if (isInvalidDate) {
                // 日期不在可選範圍內, 跳光箱
                setOpenAlertModal(true)
                setClickItemCondition(true)
                return
            }

            // 修改產品區塊的內容
            const { SeekDestinations } = UrlParams;
            const updatedSeekDestinations = SeekDestinations.length > 1 ?
                [ // 依照是否為來回或單程，更新SeekDestinations 
                    { ...SeekDestinations[0], DepartDate: selectedDate[0].departDate },
                    { ...SeekDestinations[1], DepartDate: selectedDate[0].returnDate ? selectedDate[0].returnDate : SeekDestinations[1].DepartDate }
                ]
                :
                [
                    { ...SeekDestinations[0], DepartDate: selectedDate[0].departDate }
                ];
            dispatch(setSearchPrice({
                ...UrlParams,
                SeekDestinations: updatedSeekDestinations
            }));
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(() => {
                const urlParams = getItineraryUrlParams();
                dispatch(setUrlparams(urlParams))
                setClickItemCondition(true)
            }, 0);
        }
    }

    const formatPrice = (price: number | string): string => {
        return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","); // 千分位前加逗號
    };

    function closeModal() {
        setOpenAlertModal(false)
    }

    const debounce = (func: Function, wait: number) => {
        let timeout: NodeJS.Timeout;
        return (...args: any[]) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), wait);
        };
    };

    function addZero(num: number) {
        return num < 10 ? `0${num}` : `${num}` // 個位數前補0
    }

    return (
        <>
            {(SearchPrice.isLoading) ?    /* SearchPrice.isLoading為true時，整個區塊呈現loading樣式。(搜尋結果區塊render完成後，此元件才打api)*/
                <div className={cN(style['carousel-wrap'], style['loading'])}>
                    <Skeleton part='rectangle' customClassName='full-height' />
                </div>
                :
                <div className={style['carousel-wrap']}>
                    <button onClick={() => moveHorizontally("clickLeft", 3)} className={cN(style['button'], style['left'], currentTranslate >= 0 && style['disabled'])} type='button' >
                        <ArrowLeftIcon fill={currentTranslate >= 0 ? '#999' : '#000'} />  {/* 移動到底時按鈕反灰 */}
                    </button>

                    <div className={style['date-wrap']} ref={dateWrapRef}>
                        <div className={style['date-container']} ref={dateContainerRef} style={{ transform: `translateX(${currentTranslate}px)` }}>
                            {carouselData.map((datum, index) => {
                                const month = (direction: 'departDate' | 'returnDate') => new Date(datum[direction]!).getMonth() + 1
                                const date = (direction: 'departDate' | 'returnDate') => new Date(datum[direction]!).getDate()

                                return (
                                    <div key={index} className={cN(style['date-cell'], datum.isSelected ? style['selected'] : '')} ref={index === 0 ? dateCellRef : null} onClick={() => clickItem(index, dateInvalid(datum))}>
                                        <div className={style['date']}>
                                            <span>{`${addZero(month("departDate")!)}月${addZero(date("departDate")!)}日`}</span>
                                            {datum.returnDate &&
                                                <>
                                                    <span> - </span>
                                                    <span>{`${addZero(month("returnDate")!)}月${addZero(date("returnDate")!)}日`}</span>
                                                </>
                                            }
                                        </div>
                                        {/*當SearchPrice.isLoading為false，SearchInfoCalendar.isLoading為true時(前後日期的金額還未從api拿到時)，僅金額區塊呈現loading樣式 */}
                                        {(!SearchPrice.isLoading && SearchInfoCalendar.isLoading) && <Skeleton part='rectangle' />}
                                        {/* 若是日期未落在最小及最大可選日區間，就算api有回傳資料，價格一律顯示'-'；若是日期落在最大或最小可選日之間，則依照api是否有價格資料來決定顯示數字金額或查價*/}
                                        {!SearchInfoCalendar.isLoading &&
                                            (dateInvalid(datum) ? <div className={style['no-price']}>-</div> :
                                                (datum.price ?
                                                    <div className={style['lowest-price']}>{`TWD ${formatPrice(datum.price)} 起`}</div>
                                                    :
                                                    <div className={style['check-price']}>查價</div>))}
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    <button onClick={() => moveHorizontally("clickRight", 3)} className={cN(style['button'], style['right'], currentTranslate <= minTranslateX && style['disabled'])} type='button'>
                        <ArrowLeftIcon fill={currentTranslate <= minTranslateX ? '#999' : '#000'} />
                    </button>

                    <button onClick={() => handleTrendModalOpen()} className={cN(style['button'], style['open-trend'])} type='button'>
                        <TrendIcon />
                        <div>票價趨勢</div>
                    </button>
                </div>
            }
            <OutOfRangeDateLightBox openAlertModal={openAlertModal} closeModal={closeModal} />
        </>
    )
}

